import React from "react";
import "./chat-box.component.scoped.scss";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { icons } from "../../../../statics/icons";
import { useTranslation } from "react-i18next";
import { useTyping } from "../../context/typing-context";
import OpenInNewOutlinedIcon from "@material-ui/icons/OpenInNewOutlined";
import { useContext } from "react";
import { GlobalContext } from "../../../../store";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";

interface ChatBoxProps {
  authorName: string;
  isUserMessage: boolean;
  messageContent: string;
  profilePicture?: string;
  timestamp?: string;
  questionId?: string;
  questionType?: "text" | "select" | "modal";
  onRepeatQuestion?: (questionId: string) => void;
  onEditQuestion?: (questionId: string) => void;
  isQuestionAnswered?: boolean;
  messageId: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  isTyping?: boolean;
  isRepeatedQuestion?: boolean;
  attachment?: File[];
  isSubmitted?: boolean;
  caseCreatedSuccessfully?: boolean;
}

const ChatBox: React.FC<ChatBoxProps> = ({
  authorName,
  isUserMessage,
  messageContent,
  profilePicture,
  timestamp,
  questionId,
  questionType,
  onRepeatQuestion,
  onEditQuestion,
  isQuestionAnswered = false,
  messageId,
  isDisabled = false,
  isFocused = false,
  isTyping = false,
  isRepeatedQuestion = false,
  attachment,
  isSubmitted = false,
  caseCreatedSuccessfully = false,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(GlobalContext);
  const [userImageError, setUserImageError] = React.useState(false);
  const {
    setIsTypingComplete,
    currentMessageId,
    setCurrentMessageId,
  } = useTyping();
  const [displayedContent, setDisplayedContent] = React.useState("");
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isTypingCompleteLocal, setIsTypingCompleteLocal] = React.useState(
    false,
  );

  // Check if this is the current message being typed
  const isCurrentMessage = currentMessageId === messageId;

  // Real-time typing effect for AI messages
  React.useEffect(() => {
    if (!isUserMessage && messageContent && !isTypingCompleteLocal) {
      if (currentIndex < messageContent.length) {
        const timer = setTimeout(() => {
          setDisplayedContent(messageContent.substring(0, currentIndex + 1));
          setCurrentIndex(currentIndex + 1);
        }, 1); // Adjust speed as needed

        return () => clearTimeout(timer);
      } else {
        setDisplayedContent(messageContent);
        setIsTypingCompleteLocal(true);
        // Update global typing state if this is the current message
        if (isCurrentMessage) {
          setIsTypingComplete(true);
        }
      }
    } else {
      setDisplayedContent(messageContent);
      setCurrentIndex(0);
    }
  }, [
    messageContent,
    currentIndex,
    isUserMessage,
    isTypingCompleteLocal,
    isCurrentMessage,
    setIsTypingComplete,
  ]);

  // Reset typing state when message changes
  React.useEffect(() => {
    setDisplayedContent(messageContent);
    setCurrentIndex(0);
    setIsTypingCompleteLocal(false);

    // Set this as the current message if it's an AI message
    if (!isUserMessage && messageContent) {
      setCurrentMessageId(messageId);
      setIsTypingComplete(false);
    }
  }, [
    messageContent,
    isUserMessage,
    messageId,
    setCurrentMessageId,
    setIsTypingComplete,
  ]);

  const handleRepeatClick = () => {
    if (questionId && onRepeatQuestion && isQuestionAnswered) {
      onRepeatQuestion(questionId);
    }
  };

  const handleEditClick = () => {
    if (questionId && onEditQuestion && isQuestionAnswered) {
      onEditQuestion(questionId);
    }
  };

  const handleCopyClick = async () => {
    if (messageContent) {
      try {
        await navigator.clipboard.writeText(messageContent);
        // You could add a toast notification here
        console.log("Message copied to clipboard");
      } catch (err) {
        console.error("Failed to copy message: ", err);
      }
    }
  };

  return (
    <Box
      className={`chat-message-container ${
        isUserMessage ? "user-container" : "ai-container"
      } ${isDisabled ? "disabled-message" : ""} ${
        isFocused ? "focused-message" : ""
      }`}
      style={{
        opacity: isDisabled && !isRepeatedQuestion ? 0.5 : 1,
        pointerEvents: isDisabled && !isRepeatedQuestion ? "none" : "auto",
        filter: isDisabled && !isRepeatedQuestion ? "blur(1px)" : "none",
        border: isFocused ? "2px solid #2196f3" : "none",
        borderRadius: isFocused ? "8px" : "0",
        padding: isFocused ? "8px" : "0",
        backgroundColor: isFocused ? "rgba(33, 150, 243, 0.1)" : "transparent",
      }}
    >
      {/* AI messages: Profile and name outside on the left */}
      {!isUserMessage && (
        <Box
          className={`sender-section ${
            isUserMessage ? "user-sender" : "ai-sender"
          }`}
        >
          <Avatar className="profile-picture">
            {authorName.charAt(0).toUpperCase() +
              authorName.charAt(1).toUpperCase()}
          </Avatar>
          <Box className="sender-details">
            <Typography className="sender-name" variant="subtitle2">
              {!isUserMessage &&
              !isTypingCompleteLocal &&
              currentIndex < (messageContent?.length || 0) ? (
                <span className="typing-name">
                  {authorName} {t("CHATBOT.IS_WRITING")}
                  <span className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </span>
              ) : (
                authorName
              )}
            </Typography>
            {timestamp && (
              <Typography className="timestamp" variant="caption">
                {timestamp}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* User messages: Profile picture only outside on the right */}
      {isUserMessage && (
        <Box
          className={`sender-section ${
            isUserMessage ? "user-sender" : "ai-sender"
          }`}
        >
          <Box className="user__profile__pic">
            {user.profilePicURL && !userImageError ? (
              <img
                src={user.profilePicURL || icons.PLACEHOLDER.user}
                title={user.firstName + " " + user.lastName}
                alt=""
                onError={setUserImageError.bind(null, true)}
              />
            ) : (
              <span className="text-uppercase">
                {user.firstName ? user.firstName[0] + user.lastName[0] : ""}
              </span>
            )}
          </Box>
        </Box>
      )}

      {/* Message text box */}
      <Box
        className={`message-box ${
          isUserMessage ? "user-message" : "ai-message"
        }`}
      >
        <Box className="message-content">
          <Typography
            className="message-text"
            variant="body1"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {displayedContent}
          </Typography>
          {/* Show attached files for user messages */}
          {isUserMessage && Array.isArray(attachment) && attachment.length > 0 && (
            <Box className="attached-files" style={{ marginTop: 8 }}>
              {attachment.map((file: File, idx: number) => {
                // If file.url exists, use it; otherwise, create a temporary URL
                const fileUrl = (file as any).url || URL.createObjectURL(file);
                return (
                  <Box key={idx}>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="attached-file-link"
                    >
                      <PictureAsPdfOutlinedIcon style={{ color: "#b444d1" }} />
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </a>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Action buttons - only show for AI messages with content after loading */}
          {!isUserMessage &&
            messageContent &&
            isTypingCompleteLocal &&
            questionId && (
              <Box className="message-actions">
                <Tooltip title={t("CHATBOT.COPY_MESSAGE") || "Copy message"}>
                  <IconButton
                    size="small"
                    className="action-button copy-button"
                    onClick={handleCopyClick}
                  >
                    <img src={icons.ChatbotCopy} alt="Copy" />
                  </IconButton>
                </Tooltip>
                <Box className="action-divider"></Box>
                {questionId && (onRepeatQuestion || onEditQuestion) && (
                  <Tooltip
                    title={
                      (isTyping
                        ? t("CHATBOT.WAIT_FOR_QUESTION_TO_FINISH_TYPING")
                        : isDisabled
                        ? t("CHATBOT.ANSWER_FOCUSED_QUESTION_FIRST")
                        : isQuestionAnswered
                        ? questionType === "modal"
                          ? t("CHATBOT.EDIT_QUESTION")
                          : questionType === "select"
                          ? t("CHATBOT.EDIT_QUESTION")
                          : t("CHATBOT.REPEAT_QUESTION")
                        : t("CHATBOT.ANSWER_QUESTION_FIRST")) as string
                    }
                  >
                    <span>
                      {/* Wrapper for disabled button */}
                      <IconButton
                        size="small"
                        className="action-button repeat-button"
                        onClick={
                          questionType === "modal"
                            ? handleEditClick
                            : handleRepeatClick
                        }
                        disabled={
                          !isQuestionAnswered ||
                          isDisabled ||
                          isTyping ||
                          isSubmitted ||
                          caseCreatedSuccessfully
                        }
                        style={{
                          opacity:
                            isQuestionAnswered &&
                            !isDisabled &&
                            !isTyping &&
                            !isSubmitted &&
                            !caseCreatedSuccessfully
                              ? 1
                              : 0.5,
                          cursor:
                            isQuestionAnswered &&
                            !isDisabled &&
                            !isTyping &&
                            !isSubmitted &&
                            !caseCreatedSuccessfully
                              ? "pointer"
                              : "not-allowed",
                        }}
                      >
                        {questionType === "modal" ? (
                          <OpenInNewOutlinedIcon className="open-modal-icon" />
                        ) : questionType === "select" ? (
                          <img src={icons.edit} alt="Edit" />
                        ) : (
                          <img src={icons.ChatbotRepeat} alt="Repeat" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatBox;
